/**
 * HR-SHIFT-NOTICE-001 — HR Announcements: composer (Shift Timing Change / DST Information),
 * audience targeting (all / teams / employees), require-acknowledgement, and ack tracking.
 * Reuses the existing announcement store + mandatory AckShell popup; HR Manager / Admin only.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Megaphone, Send, Users, Building2, User, CalendarCheck, Globe2, Clock3, CheckCircle2, Hourglass, Eye, Loader2, Search, ShieldCheck, Info } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Switch } from '../../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { formatDateTimeShortET, formatDateOnly } from '../../../utils/formatters';
import './hr-announcements.css';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const KINDS = [
  { id: 'hr_shift_notice', label: 'Shift Timing Change', hint: 'Company or team working-hours change' },
  { id: 'hr_dst_notice', label: 'DST Information', hint: 'Daylight saving reminder or guidance' },
];
const KIND_LABEL = { hr_shift_notice: 'Shift Timing Change', hr_dst_notice: 'DST Information', shift_change: 'Employee Shift Change' };
const EMPTY = { title: '', content: '', kind: 'hr_shift_notice', priority: 'important', effective_date: '', previous_hours: '', new_hours: '', timezone: '', audience_type: 'all', team_ids: [], user_ids: [], requires_ack: true };

const Field = ({ label, children, testId, hint }) => (
  <label className="hra-field" data-testid={testId}>
    <span className="hra-label">{label}{hint && <span className="hra-hint">{hint}</span>}</span>
    {children}
  </label>
);

export default function HRAnnouncements() {
  const [form, setForm] = useState(EMPTY);
  const [targets, setTargets] = useState({ teams: [], users: [], active_user_count: 0, timezones: [] });
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [acksFor, setAcksFor] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const [t, l] = await Promise.all([
        axios.get(`${API}/api/hr/announcements/targets`, { headers: authHeaders() }),
        axios.get(`${API}/api/hr/announcements`, { headers: authHeaders() }),
      ]);
      setTargets(t.data); setList(l.data.announcements || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load announcements'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const recipientCount = useMemo(() => {
    if (form.audience_type === 'all') return targets.active_user_count;
    if (form.audience_type === 'teams') return targets.users.filter((u) => form.team_ids.includes(u.team_id)).length;
    return form.user_ids.length;
  }, [form, targets]);
  const toggle = (k, id) => set(k, form[k].includes(id) ? form[k].filter((x) => x !== id) : [...form[k], id]);
  const filteredUsers = targets.users.filter((u) => !search || (u.full_name || u.email || '').toLowerCase().includes(search.toLowerCase()));
  const isShift = form.kind === 'hr_shift_notice';

  const publish = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error('Title and message are required'); return; }
    if (form.audience_type === 'teams' && !form.team_ids.length) { toast.error('Select at least one team'); return; }
    if (form.audience_type === 'users' && !form.user_ids.length) { toast.error('Select at least one employee'); return; }
    setSending(true);
    try {
      const payload = {
        title: form.title, content: form.content, kind: form.kind, priority: form.priority,
        effective_date: form.effective_date || null, previous_hours: form.previous_hours || null, new_hours: form.new_hours || null,
        timezone: form.timezone || null, requires_ack: form.requires_ack,
        audience: { type: form.audience_type, team_ids: form.team_ids, user_ids: form.user_ids },
      };
      const r = await axios.post(`${API}/api/hr/announcements`, payload, { headers: authHeaders() });
      toast.success(`Announcement sent to ${r.data.recipient_count} employee${r.data.recipient_count === 1 ? '' : 's'}`, {
        description: r.data.ack_downgraded ? 'Sent as informational — the automatic DST notice already required acknowledgement for this event.' : (r.data.requires_ack ? 'Each recipient must acknowledge it.' : 'Informational — no acknowledgement required.'),
      });
      setForm(EMPTY); setSearch('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to publish announcement'); }
    finally { setSending(false); }
  };

  return (
    <div className="hra-page" data-testid="hr-announcements-page">
      <div className="hra-head">
        <div className="hra-head-icon"><Megaphone className="w-5 h-5" /></div>
        <div>
          <h1 className="text-[22px] font-bold text-slate-900 tracking-tight leading-tight" data-testid="hr-announcements-title">HR Announcements</h1>
          <p className="text-sm text-slate-500">Communicate shift timing changes and DST information to all employees, selected teams or individuals — with acknowledgement tracking.</p>
        </div>
      </div>

      <div className="hra-grid">
        {/* Composer */}
        <section className="hra-card hra-composer" data-testid="hr-announcement-composer">
          <div className="hra-card-title"><Send className="w-4 h-4" /> New Announcement</div>
          <div className="hra-kinds" role="radiogroup">
            {KINDS.map((k) => (
              <button key={k.id} type="button" role="radio" aria-checked={form.kind === k.id} onClick={() => set('kind', k.id)}
                className={`hra-kind ${form.kind === k.id ? 'is-active' : ''}`} data-testid={`hr-kind-${k.id}`}>
                {k.id === 'hr_shift_notice' ? <Clock3 className="w-4 h-4" /> : <Globe2 className="w-4 h-4" />}
                <span><strong>{k.label}</strong><small>{k.hint}</small></span>
              </button>
            ))}
          </div>
          <Field label="Title" testId="hr-field-title">
            <Input value={form.title} onChange={(e) => set('title', e.target.value)} maxLength={200} placeholder={isShift ? 'e.g. Tax Team shift moves to 8:00 AM – 5:00 PM' : 'e.g. Daylight Saving Time ends Sunday'} data-testid="hr-input-title" />
          </Field>
          <Field label="Message" testId="hr-field-content">
            <Textarea value={form.content} onChange={(e) => set('content', e.target.value)} rows={4} maxLength={5000} placeholder="What is changing, why, and what employees should do." data-testid="hr-input-content" />
          </Field>
          <div className="hra-two">
            <Field label="Effective Date" testId="hr-field-effective">
              <Input type="date" value={form.effective_date} onChange={(e) => set('effective_date', e.target.value)} data-testid="hr-input-effective-date" />
            </Field>
            <Field label="Timezone" testId="hr-field-timezone">
              <Input list="hra-tz" value={form.timezone} onChange={(e) => set('timezone', e.target.value)} placeholder="America/New_York" data-testid="hr-input-timezone" />
              <datalist id="hra-tz">{targets.timezones.map((z) => <option key={z} value={z} />)}</datalist>
            </Field>
          </div>
          {isShift && (
            <div className="hra-two">
              <Field label="Previous Hours" testId="hr-field-previous">
                <Input value={form.previous_hours} onChange={(e) => set('previous_hours', e.target.value)} placeholder="9:00 AM – 6:00 PM" data-testid="hr-input-previous-hours" />
              </Field>
              <Field label="New Hours" testId="hr-field-new">
                <Input value={form.new_hours} onChange={(e) => set('new_hours', e.target.value)} placeholder="8:00 AM – 5:00 PM" data-testid="hr-input-new-hours" />
              </Field>
            </div>
          )}

          <div className="hra-label mt-1">Audience</div>
          <div className="hra-audience" role="radiogroup" data-testid="hr-audience">
            {[['all', Users, 'All Employees'], ['teams', Building2, 'Selected Teams'], ['users', User, 'Selected Employees']].map(([id, Icon, label]) => (
              <button key={id} type="button" role="radio" aria-checked={form.audience_type === id} onClick={() => set('audience_type', id)}
                className={`hra-aud ${form.audience_type === id ? 'is-active' : ''}`} data-testid={`hr-audience-${id}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
          {form.audience_type === 'teams' && (
            <div className="hra-picker" data-testid="hr-team-picker">
              {targets.teams.map((t) => (
                <label key={t.id} className={`hra-chip ${form.team_ids.includes(t.id) ? 'is-on' : ''}`} data-testid={`hr-team-${t.id}`}>
                  <input type="checkbox" checked={form.team_ids.includes(t.id)} onChange={() => toggle('team_ids', t.id)} />
                  {t.name} <span className="hra-chip-count">{t.member_count}</span>
                </label>
              ))}
              {!targets.teams.length && <span className="text-xs text-slate-500">No teams configured.</span>}
            </div>
          )}
          {form.audience_type === 'users' && (
            <div className="hra-picker hra-picker-users" data-testid="hr-user-picker">
              <div className="hra-search"><Search className="w-3.5 h-3.5" /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employees…" data-testid="hr-user-search" /></div>
              <div className="hra-user-list">
                {filteredUsers.slice(0, 60).map((u) => (
                  <label key={u.id} className={`hra-user ${form.user_ids.includes(u.id) ? 'is-on' : ''}`} data-testid={`hr-user-${u.id}`}>
                    <input type="checkbox" checked={form.user_ids.includes(u.id)} onChange={() => toggle('user_ids', u.id)} />
                    <span className="hra-user-name">{u.full_name || u.email}</span>
                    <span className="hra-user-team">{u.team_name || '—'}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="hra-footer">
            <label className="hra-switch" data-testid="hr-requires-ack">
              <Switch checked={form.requires_ack} onCheckedChange={(v) => set('requires_ack', v)} data-testid="hr-requires-ack-switch" />
              <span><ShieldCheck className="w-4 h-4" /> Require acknowledgement</span>
            </label>
            <div className="hra-send">
              <span className="hra-count" data-testid="hr-recipient-count">{recipientCount} recipient{recipientCount === 1 ? '' : 's'}</span>
              <Button onClick={publish} disabled={sending} className="hra-publish" data-testid="hr-publish-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Publish
              </Button>
            </div>
          </div>
        </section>

        {/* History / tracking */}
        <section className="hra-card" data-testid="hr-announcement-list">
          <div className="hra-card-title"><Hourglass className="w-4 h-4" /> Sent Announcements <span className="hra-chip-count">{list.length}</span></div>
          {loading ? <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
            : !list.length ? <div className="hra-empty" data-testid="hr-announcement-empty"><Info className="w-4 h-4" /> No shift or DST announcements yet.</div>
            : list.map((a) => {
              const pct = a.recipient_count ? Math.round((a.ack_count / a.recipient_count) * 100) : 0;
              return (
                <button type="button" key={a.id} className="hra-item" onClick={() => setAcksFor(a)} data-testid={`hr-announcement-item-${a.id}`}>
                  <div className="hra-item-top">
                    <span className={`hra-badge kind-${a.kind}`}>{KIND_LABEL[a.kind] || 'Announcement'}</span>
                    <span className="hra-item-date">{formatDateTimeShortET(a.created_at)}</span>
                  </div>
                  <div className="hra-item-title">{a.kind === 'shift_change' ? `${a.shift_change?.employee_name} — ${a.shift_change?.new_shift}` : a.title}</div>
                  <div className="hra-item-meta">
                    <span>{a.audience === 'all' ? 'All employees' : a.audience_spec?.type === 'teams' ? 'Selected teams' : a.audience_spec?.type === 'users' ? 'Selected employees' : 'Targeted'}</span>
                    {(a.hr_notice?.effective_date || a.shift_change?.effective_from) && <span><CalendarCheck className="w-3 h-3" /> {formatDateOnly(a.hr_notice?.effective_date || a.shift_change?.effective_from)}</span>}
                    <span>by {a.created_by_name}</span>
                  </div>
                  {a.requires_ack ? (
                    <div className="hra-progress" data-testid={`hr-ack-progress-${a.id}`}>
                      <div className="hra-bar"><span style={{ width: `${pct}%` }} /></div>
                      <span className="hra-prog-ok"><CheckCircle2 className="w-3.5 h-3.5" /> {a.ack_count} acknowledged</span>
                      <span className="hra-prog-pending"><Hourglass className="w-3.5 h-3.5" /> {a.pending_count} pending</span>
                    </div>
                  ) : <div className="hra-info-tag"><Eye className="w-3.5 h-3.5" /> Informational · {a.recipient_count} recipients</div>}
                </button>
              );
            })}
        </section>
      </div>

      <AckTrackingDialog item={acksFor} onClose={() => setAcksFor(null)} />
    </div>
  );
}

const AckTrackingDialog = ({ item, onClose }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('pending');
  useEffect(() => {
    if (!item) { setData(null); return; }
    setTab('pending');
    axios.get(`${API}/api/hr/announcements/${item.id}/acks`, { headers: authHeaders() })
      .then((r) => setData(r.data)).catch(() => toast.error('Failed to load acknowledgements'));
  }, [item]);
  if (!item) return null;
  const rows = data ? (tab === 'acked' ? data.acknowledged : tab === 'pending' ? data.pending : data.informational) : [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="hra-dialog" data-testid="hr-ack-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900"><ShieldCheck className="w-5 h-5 text-blue-600" /> Acknowledgement Tracking</DialogTitle>
          <DialogDescription>{item.kind === 'shift_change' ? `${item.shift_change?.employee_name} — ${item.shift_change?.new_shift}` : item.title}</DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-8 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
          <>
            <div className="hra-stats">
              <div className="hra-stat"><span>{data.recipient_count}</span>Recipients</div>
              <div className="hra-stat ok" data-testid="hr-ack-dialog-acked"><span>{data.ack_count}</span>Acknowledged</div>
              <div className="hra-stat warn" data-testid="hr-ack-dialog-pending"><span>{data.pending_count}</span>Pending</div>
            </div>
            <div className="hra-tabs">
              {[['pending', `Pending (${data.pending.length})`], ['acked', `Acknowledged (${data.acknowledged.length})`], ['info', `Informational (${data.informational.length})`]].map(([id, label]) => (
                <button key={id} type="button" className={tab === id ? 'is-active' : ''} onClick={() => setTab(id)} data-testid={`hr-ack-tab-${id}`}>{label}</button>
              ))}
            </div>
            <div className="hra-ack-list" data-testid="hr-ack-list">
              {!rows.length && <div className="hra-empty"><Info className="w-4 h-4" /> Nobody in this list.</div>}
              {rows.map((r) => (
                <div key={r.user_id} className="hra-ack-row" data-testid={`hr-ack-row-${r.user_id}`}>
                  <span className="hra-ack-name">{r.name}</span>
                  <span className="hra-ack-team">{r.team || '—'}</span>
                  <span className={`hra-ack-when ${r.acknowledged_at ? 'ok' : ''}`}>
                    {r.acknowledged_at ? <><CheckCircle2 className="w-3.5 h-3.5" /> {formatDateTimeShortET(r.acknowledged_at)}</> : r.read_at ? `Read ${formatDateTimeShortET(r.read_at)}` : 'Not yet opened'}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
